import {
  type RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import {
  collectValues,
  emptyQuestionState,
  formatAskUserQuestionAnswers,
  type QuestionState,
} from 'features/ask-yogi/components/ask-user-question-answers'
import type {
  AskUserQuestion,
  AskUserQuestionArtifact,
  AskUserQuestionOption,
} from 'features/ask-yogi/types'

const isTextFieldFocused = (): boolean => {
  const el = document.activeElement
  if (!el) return false
  const tag = el.tagName
  return (
    tag === 'INPUT' ||
    tag === 'TEXTAREA' ||
    (el as HTMLElement).isContentEditable
  )
}

export type UseAskUserInputWidgetArgs = {
  artifact: AskUserQuestionArtifact
  onSubmit?: (content: string) => void
  disabled?: boolean
}

export type UseAskUserInputWidgetResult = {
  questions: AskUserQuestion[]
  currentIndex: number
  keyboardIndex: number
  direction: 'forward' | 'back'
  question: AskUserQuestion | undefined
  state: QuestionState
  options: AskUserQuestionOption[]
  isReadOnly: boolean
  isPaged: boolean
  isFirstPage: boolean
  isLastPage: boolean
  isOtherKeyboardFocused: boolean
  hasOtherText: boolean
  hasAnyAnswer: boolean
  showSubmit: boolean
  containerRef: RefObject<HTMLDivElement>
  inputRef: RefObject<HTMLInputElement>
  goBack: () => void
  goNext: () => void
  activateOption: (label: string) => void
  handleOtherChange: (text: string) => void
  commitOther: () => void
  submitAll: () => void
}

export const useAskUserInputWidget = ({
  artifact,
  onSubmit,
  disabled = false,
}: UseAskUserInputWidgetArgs): UseAskUserInputWidgetResult => {
  const { questions } = artifact
  const [states, setStates] = useState<QuestionState[]>(() =>
    questions.map(() => emptyQuestionState()),
  )
  // A read-only widget (a prior turn's questions, re-mounted on load) starts on
  // the last question so the user sees the answered end of the flow, not the top.
  const [currentIndex, setCurrentIndex] = useState(() =>
    disabled ? Math.max(questions.length - 1, 0) : 0,
  )
  const [keyboardIndex, setKeyboardIndex] = useState(0)
  const [submitted, setSubmitted] = useState(false)
  const directionRef = useRef<'forward' | 'back'>('forward')
  const inputRef = useRef<HTMLInputElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const autoFocusedRef = useRef(false)

  const isReadOnly = disabled || !onSubmit || submitted
  const isPaged = questions.length > 1
  const isFirstPage = currentIndex === 0
  const isLastPage = currentIndex === questions.length - 1

  const question = questions[currentIndex]
  const state = states[currentIndex] ?? emptyQuestionState()
  const options = useMemo(() => question?.options ?? [], [question])
  const otherRowIndex = options.length
  const isOtherKeyboardFocused = keyboardIndex === otherRowIndex
  const hasOtherText = state.otherText.trim().length > 0

  const hasAnyAnswer = useMemo(
    () => states.some((s) => collectValues(s).length > 0),
    [states],
  )

  const updateCurrent = useCallback(
    (patch: (prev: QuestionState) => QuestionState): void => {
      setStates((prev) =>
        prev.map((s, i) =>
          i === currentIndex ? patch(s ?? emptyQuestionState()) : s,
        ),
      )
    },
    [currentIndex],
  )

  const goToPage = useCallback(
    (next: number, direction: 'forward' | 'back'): void => {
      directionRef.current = direction
      setCurrentIndex(next)
      // Position the cursor at the previously-selected row (or 0 if unanswered)
      // so returning to an answered page doesn't shade two rows at once.
      const nextState = states[next] ?? emptyQuestionState()
      const nextQuestion = questions[next]
      let nextCursor = 0
      if (nextQuestion) {
        if (nextState.otherActive) {
          nextCursor = nextQuestion.options.length
        } else {
          const idx = nextQuestion.options.findIndex((o) =>
            nextState.selectedLabels.includes(o.label),
          )
          if (idx >= 0) nextCursor = idx
        }
      }
      setKeyboardIndex(nextCursor)
      containerRef.current?.focus()
    },
    [states, questions],
  )

  const goNext = useCallback((): void => {
    if (isLastPage) return
    goToPage(currentIndex + 1, 'forward')
  }, [currentIndex, isLastPage, goToPage])

  const goBack = useCallback((): void => {
    if (isFirstPage) return
    goToPage(currentIndex - 1, 'back')
  }, [currentIndex, isFirstPage, goToPage])

  const submitWith = useCallback(
    (finalStates: QuestionState[]): void => {
      if (isReadOnly || !onSubmit) return
      const hasAny = finalStates.some((s) => collectValues(s).length > 0)
      if (!hasAny) return
      onSubmit(formatAskUserQuestionAnswers(questions, finalStates))
      setSubmitted(true)
    },
    [isReadOnly, onSubmit, questions],
  )

  const submitAll = useCallback(() => submitWith(states), [submitWith, states])

  // Single-select: record the pick and auto-advance (or submit on the last
  // question). Multi-select: toggle without advancing.
  const activateOption = useCallback(
    (label: string): void => {
      if (isReadOnly || !question) return
      setKeyboardIndex(-1)
      containerRef.current?.focus()
      if (question.multi_select) {
        updateCurrent((prev) => ({
          ...prev,
          selectedLabels: prev.selectedLabels.includes(label)
            ? prev.selectedLabels.filter((l) => l !== label)
            : [...prev.selectedLabels, label],
        }))
        return
      }
      const next = states.map((s, i) =>
        i === currentIndex
          ? { ...s, selectedLabels: [label], otherActive: false }
          : s,
      )
      setStates(next)
      if (isLastPage) submitWith(next)
      else goToPage(currentIndex + 1, 'forward')
    },
    [
      isReadOnly,
      question,
      updateCurrent,
      states,
      currentIndex,
      isLastPage,
      submitWith,
      goToPage,
    ],
  )

  const handleOtherChange = useCallback(
    (text: string): void => {
      if (isReadOnly || !question) return
      const active = text.trim().length > 0
      updateCurrent((prev) =>
        question.multi_select
          ? { ...prev, otherText: text, otherActive: active }
          : // Single-select: free text is exclusive with the options.
            {
              ...prev,
              otherText: text,
              otherActive: active,
              selectedLabels: [],
            },
      )
    },
    [isReadOnly, question, updateCurrent],
  )

  // Commit the single-select free-text answer (Enter / arrow button): advance
  // or submit, mirroring an option pick.
  const commitOther = useCallback((): void => {
    if (isReadOnly || !question || question.multi_select) return
    const text = state.otherText.trim()
    if (!text) return
    const next = states.map((s, i) =>
      i === currentIndex
        ? { ...s, selectedLabels: [], otherActive: true, otherText: text }
        : s,
    )
    setStates(next)
    if (isLastPage) submitWith(next)
    else goToPage(currentIndex + 1, 'forward')
  }, [
    isReadOnly,
    question,
    state.otherText,
    states,
    currentIndex,
    isLastPage,
    submitWith,
    goToPage,
  ])

  // Keyboard-nav to the Other row focuses the input so the user can type
  // immediately. Mouse-click on the input handles its own DOM focus; arrow-key
  // handlers restore container focus so subsequent shortcuts keep responding.
  useEffect(() => {
    if (isOtherKeyboardFocused) inputRef.current?.focus()
  }, [isOtherKeyboardFocused, currentIndex])

  // Autofocus on mount so keyboard nav works immediately. Pulling focus off the
  // main chat input is intended — the assistant just asked a question.
  useEffect(() => {
    if (autoFocusedRef.current || isReadOnly) return
    autoFocusedRef.current = true
    containerRef.current?.focus()
  }, [isReadOnly])

  useEffect(() => {
    const totalRows = options.length + 1
    const handleKeyDown = (e: KeyboardEvent): void => {
      // Self-contained: only act while focus is within this widget's subtree,
      // so a second mounted widget never also responds to the keypress.
      if (!containerRef.current?.contains(document.activeElement)) return
      const typing = isTextFieldFocused()
      // Left/right paging stays live post-submit so users can review prior answers.
      if (isPaged && !typing && e.key === 'ArrowLeft') {
        e.preventDefault()
        goBack()
        return
      }
      if (isPaged && !typing && e.key === 'ArrowRight') {
        e.preventDefault()
        goNext()
        return
      }
      if (isReadOnly) return
      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault()
        submitWith(states)
        return
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        containerRef.current?.focus()
        setKeyboardIndex((i) => (i + 1) % totalRows)
        return
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault()
        containerRef.current?.focus()
        setKeyboardIndex((i) => (i <= 0 ? totalRows - 1 : i - 1))
        return
      }
      if (e.key === 'Enter') {
        e.preventDefault()
        if (document.activeElement === inputRef.current) {
          commitOther()
        } else if (keyboardIndex >= 0) {
          const opt = options[keyboardIndex]
          if (opt) activateOption(opt.label)
        }
        return
      }
      if (
        !typing &&
        /^[1-9]$/.test(e.key) &&
        !e.metaKey &&
        !e.ctrlKey &&
        !e.altKey
      ) {
        const idx = Number(e.key) - 1
        const opt = options[idx]
        if (opt) {
          e.preventDefault()
          activateOption(opt.label)
        }
        return
      }
      // Escape skips the current question (advisory — omitted from payload).
      if (e.key === 'Escape' && isPaged && !isLastPage) {
        e.preventDefault()
        goNext()
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [
    isReadOnly,
    isPaged,
    isLastPage,
    options,
    keyboardIndex,
    states,
    submitWith,
    goBack,
    goNext,
    commitOther,
    activateOption,
  ])

  const showSubmit = !!question?.multi_select || isPaged

  return {
    questions,
    currentIndex,
    keyboardIndex,
    direction: directionRef.current,
    question,
    state,
    options,
    isReadOnly,
    isPaged,
    isFirstPage,
    isLastPage,
    isOtherKeyboardFocused,
    hasOtherText,
    hasAnyAnswer,
    showSubmit,
    containerRef,
    inputRef,
    goBack,
    goNext,
    activateOption,
    handleOtherChange,
    commitOther,
    submitAll,
  }
}
